import client from "./client";

export type ComplianceCategory =
  | "certification"
  | "training"
  | "license"
  | "clearance";

export type ComplianceStatus =
  | "pending"
  | "completed"
  | "expired"
  | "waived";

export interface ComplianceRequirement {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  category: ComplianceCategory;
  required_for_all: boolean;
  required_for_departments: string[] | null;
  required_for_titles: string[] | null;
  renewal_interval: "annual" | "biannual" | "custom" | null;
  renewal_days: number | null;
  grace_period_days: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmployeeComplianceRecord {
  id: string;
  org_id: string;
  employee_id: string;
  requirement_id: string;
  status: ComplianceStatus;
  completed_at: string | null;
  expires_at: string | null;
  certificate_url: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  requirement?: {
    id: string;
    name: string;
    category: string;
    renewal_interval: string | null;
    grace_period_days: number;
  } | null;
  employee?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    work_email: string | null;
  } | null;
  is_expiring_soon: boolean;
  is_expired: boolean;
}

export interface ComplianceSummary {
  total: number;
  completed: number;
  pending: number;
  expired: number;
  expiring_soon: number;
  percent_complete: number;
}

// ── Requirements ───────────────────────────────────────────────────

export async function listRequirements(): Promise<ComplianceRequirement[]> {
  const res = await client.get<ComplianceRequirement[]>(
    "/compliance-requirements",
  );
  return res.data;
}

export async function createRequirement(
  payload: Omit<
    ComplianceRequirement,
    "id" | "org_id" | "is_active" | "created_at" | "updated_at"
  > & { is_active?: boolean },
): Promise<ComplianceRequirement> {
  const res = await client.post<ComplianceRequirement>(
    "/compliance-requirements",
    payload,
  );
  return res.data;
}

export async function updateRequirement(
  id: string,
  patch: Partial<ComplianceRequirement>,
): Promise<ComplianceRequirement> {
  const res = await client.put<ComplianceRequirement>(
    `/compliance-requirements/${id}`,
    patch,
  );
  return res.data;
}

export async function deleteRequirement(id: string): Promise<void> {
  await client.delete(`/compliance-requirements/${id}`);
}

// ── Employee records ──────────────────────────────────────────────

export async function listEmployeeCompliance(params?: {
  employee_id?: string;
  requirement_id?: string;
  status?: ComplianceStatus;
}): Promise<EmployeeComplianceRecord[]> {
  const res = await client.get<EmployeeComplianceRecord[]>(
    "/employee-compliance",
    { params },
  );
  return res.data;
}

export async function createEmployeeCompliance(
  payload: Partial<EmployeeComplianceRecord> & {
    employee_id: string;
    requirement_id: string;
  },
): Promise<EmployeeComplianceRecord> {
  const res = await client.post<EmployeeComplianceRecord>(
    "/employee-compliance",
    payload,
  );
  return res.data;
}

export async function updateEmployeeCompliance(
  id: string,
  patch: Partial<EmployeeComplianceRecord>,
): Promise<EmployeeComplianceRecord> {
  const res = await client.put<EmployeeComplianceRecord>(
    `/employee-compliance/${id}`,
    patch,
  );
  return res.data;
}

export async function deleteEmployeeCompliance(id: string): Promise<void> {
  await client.delete(`/employee-compliance/${id}`);
}

export async function getComplianceSummary(): Promise<ComplianceSummary> {
  const res = await client.get<ComplianceSummary>("/compliance/summary");
  return res.data;
}
